import {days, questions} from '@components/competitions/levels/muharram/assets/questions';
import {useCompetitionMuharramContext} from '@context/CompetitionMuharramContext';
import CloseIcon from '@icons/CloseIcon';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Slide,
	useMediaQuery,
	useTheme
} from '@mui/material';
import {TransitionProps} from '@mui/material/transitions';
import React, {forwardRef, ReactElement, Ref, useState} from 'react';
import {useTranslation} from 'react-i18next';

interface DaysListProps {
	onSelectDay: (dayId: number) => void;
}

const allowedDays: number[] = [5, 6, 7, 8, 9, 10,11, 12, 13, 14];

const normalizeDay = (day: number): number => {
	return day <= 6 ? day + 30 : day;
};

const Transition = forwardRef(function Transition(
	props: TransitionProps & {children: ReactElement<any, any>},
	ref: Ref<unknown>
) {
	return <Slide direction="up" ref={ref} {...props} />;
});

export const DaysListCol = ({onSelectDay}: DaysListProps) => {
	const {t} = useTranslation();
	const theme = useTheme();
	const {answers} = useCompetitionMuharramContext();
	
	const [open, setOpen] = useState(false);
	const [selectedDayId, setSelectedDayId] = useState<number | null>(null);
	const [viewAnswersDayId, setViewAnswersDayId] = useState<number | null>(null);
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
	
	const filteredEntries = allowedDays
		.filter((key) => days[key])
		.map((key) => [key.toString(), days[key]] as [string, typeof days[number]]);
	
	const handleDayClick = (dayId: number, isAnswered: boolean) => {
		if (isAnswered) {
			setViewAnswersDayId(dayId);
		} else {
			setSelectedDayId(dayId);
			setOpen(true);
		}
	};
	
	const handleClose = () => {
		setOpen(false);
		setSelectedDayId(null);
	};
	
	const handleUnderstood = () => {
		if (selectedDayId !== null) {
			onSelectDay(selectedDayId);
		}
		handleClose();
	};
	
	const selectedDay = selectedDayId !== null ? days[selectedDayId] : null;
	
	return (
		<div className="flex flex-col gap-y-4 w-full py-10">
			<Dialog
				open={viewAnswersDayId !== null}
				onClose={() => setViewAnswersDayId(null)}
				fullScreen={fullScreen}
				TransitionComponent={Transition}
				aria-labelledby="dialog-view-answers"
			>
				<DialogTitle>{viewAnswersDayId && days[viewAnswersDayId]?.title}</DialogTitle>
				<DialogContent dividers>
					<div className={'font-bold text-center mb-5'}>
						{viewAnswersDayId && days[viewAnswersDayId]?.subject}
					</div>
					{viewAnswersDayId && questions[viewAnswersDayId]?.map((q, index) => {
						const userAnswers = answers[viewAnswersDayId] || {};
						const userAnswerIndex = userAnswers[index];
						const correctAnswerIndex = q.correctAnswer;
						
						const isUnanswered = userAnswerIndex === null || userAnswerIndex === undefined;
						const isWrong = !isUnanswered && userAnswerIndex !== correctAnswerIndex;
						
						return (
							<div key={index} className="mb-4">
								<p className="font-semibold mb-1">{index + 1}. {q.question}</p>
								
								<p className="text-green-700">
									✓ {q.answers[correctAnswerIndex]}
								</p>
								
								{isWrong && (
									<p className="text-red-600">
										✗ {q.answers[userAnswerIndex]}
									</p>
								)}
								
								{isUnanswered && (
									<p className="text-gray-400 italic">
										پاسخ داده نشده
									</p>
								)}
							</div>
						);
					})}
				</DialogContent>
				<DialogActions sx={{justifyContent: 'flex-start'}}>
					<Button onClick={() => setViewAnswersDayId(null)} variant="outlined" sx={{
						color: '#0f5132',
						borderColor: '#0f5132',
						'&:hover': {borderColor: '#0f5132', backgroundColor: '#d1e7dd'}
					}}>
						{t('competitions.close')}
					</Button>
				</DialogActions>
			</Dialog>
			
			<Dialog
				fullScreen={false}
				open={open}
				onClose={handleClose}
				scroll="paper"
				TransitionComponent={Transition}
				keepMounted
				aria-labelledby="responsive-dialog-title"
				aria-describedby="alert-dialog-slide-description"
			>
				<DialogTitle id="responsive-dialog-title" dangerouslySetInnerHTML={{
					__html: selectedDayId !== null ? days[selectedDayId]?.title || '' : ''
				}}/>
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500]}}
				>
					<CloseIcon/>
				</IconButton>
				<DialogContent dividers>
					<div className={'font-bold'}>
						{selectedDayId !== null ? days[selectedDayId]?.subject || '' : ''}
					</div>
					<DialogContentText className={'text-base text-justify'}
					                   dangerouslySetInnerHTML={{
						                   __html: selectedDayId !== null ? days[selectedDayId]?.description || '' : ''
					                   }}
					/>
				</DialogContent>
				<DialogActions sx={{justifyContent: 'flex-start'}}>
					<Button
						autoFocus
						onClick={handleUnderstood}
						variant="outlined"
						sx={{
							color: '#0f5132',
							borderColor: '#0f5132',
							'&:hover': {
								borderColor: '#0f5132',
								backgroundColor: '#d1e7dd'
							},
							'&.Mui-focused': {
								borderColor: '#0f5132'
							},
							'&.MuiButton-outlinedPrimary': {
								borderColor: '#0f5132'
							},
							'&:focus-visible': {
								outline: '2px solid #0f5132',
								outlineOffset: '2px'
							}
						}}
					>
						{t('competitions.understood')}
					</Button>
				</DialogActions>
			</Dialog>
			
			{filteredEntries.map(([key, day]) => {
				const dayId = Number(key);
				const isAnswered = answers[dayId] !== undefined;
				const imageName = `d${dayId}-${isAnswered ? 'dis' : 'en'}.webp`; // Keep distinction if needed
				const imageSrc = `/images/muharram/${imageName}`;
				const isEven = dayId % 2 === 0;
				
				return (
					<div
						key={key}
						onClick={() => handleDayClick(dayId, isAnswered)}
						className="w-full relative transition cursor-pointer"
					>
						<img src={imageSrc} alt={`Day ${dayId}`} className="w-full h-auto object-contain"/>
						{isAnswered && (
							<span
								className={`absolute top-0 ${isEven ? 'right-2' : 'left-2'} bg-green-600 text-white text-lg font-bold px-2 py-0 rounded-full shadow-md`}>
					✓
				</span>
						)}
					</div>
				);
			})}
		</div>
	);
};
